import { useState } from 'react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import cases from '../../data/cases.json';

const CasesPage = () => {
  const [visibleCases, setVisibleCases] = useState(3);
  // Função para converter data em formato brasileiro para Date object
  const parseDate = (dateStr: string): Date => {
    const months: { [key: string]: number } = {
      'Jan': 0, 'Fev': 1, 'Mar': 2, 'Abr': 3, 'Mai': 4, 'Jun': 5,
      'Jul': 6, 'Ago': 7, 'Set': 8, 'Out': 9, 'Nov': 10, 'Dez': 11
    };
    const parts = dateStr.split(' ');
    const day = parseInt(parts[0]);
    const month = months[parts[1]];
    const year = parseInt(parts[2]);
    return new Date(year, month, day);
  };

  // Ordenar cases do mais recente para o mais antigo
  const sortedCases = [...cases].sort((a, b) => {
    return parseDate(b.date).getTime() - parseDate(a.date).getTime();
  });
  return (
    <div className="min-h-screen bg-white">
      <Header bannerHeightPercent={0.5} />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-[#161e2d] py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 bg-[#7d2128] rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-40 h-40 bg-[#7d2128] rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 w-48 h-48 bg-blue-600 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium mb-6 text-white">
              <i className="ri-trophy-line mr-2"></i>
              Histórias de Sucesso & Transformação
            </div>
            <h1 className="font-bold text-white mb-6 leading-tight">
              <span className="text-xl lg:text-2xl block mb-2">Conheça nossas</span>
              <span className="text-4xl lg:text-6xl text-[#7d2128] block">Histórias de Sucesso</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Descubra como transformamos desafios em resultados extraordinários através de soluções inovadoras e personalizadas para cada cliente.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center text-gray-400">
              <div className="flex items-center">
                <i className="ri-bar-chart-line text-green-400 mr-2 text-xl"></i>
                <span>Resultados Comprovados</span>
              </div>
              <div className="flex items-center">
                <i className="ri-team-line text-green-400 mr-2 text-xl"></i>
                <span>Parcerias de Longo Prazo</span>
              </div>
              <div className="flex items-center">
                <i className="ri-lightbulb-line text-green-400 mr-2 text-xl"></i>
                <span>Inovação Constante</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cases List Section */}
      <section className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Histórias de Transformação</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Cada case representa um desafio único e uma solução personalizada. Clique em qualquer case para conhecer os detalhes completos da implementação.
            </p>
          </div>

          <div className="space-y-8">
            {sortedCases.slice(0, visibleCases).map((caseItem) => (
              <a
                key={caseItem.id}
                href={`/cases/${caseItem.slug}`}
                className="group block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 cursor-pointer"
              >
                <div className="md:flex">
                  {/* Image Section */}
                  <div className="md:w-2/5 relative overflow-hidden">
                    <div className="aspect-[4/3] bg-gradient-to-br from-gray-100 to-gray-200">
                      <img
                        src={caseItem.image}
                        alt={caseItem.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    {/* Category Badge */}
                    <div className="absolute top-4 left-4">
                      <span className="inline-block px-3 py-1 bg-[#7d2128]/90 text-white text-sm font-medium rounded-full">
                        {caseItem.category}
                      </span>
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="md:w-3/5 p-6 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <div className="flex items-center">
                            <i className="ri-calendar-line mr-1"></i>
                            <span>{caseItem.date}</span>
                          </div>
                          <div className="flex items-center">
                            <i className="ri-time-line mr-1"></i>
                            <span>{caseItem.readTime}</span>
                          </div>
                        </div>
                      </div>

                      <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-[#7d2128] transition-colors duration-300">
                        {caseItem.title}
                      </h3>

                      <p className="text-gray-600 text-lg leading-relaxed mb-6">
                        {caseItem.description}
                      </p>

                      {/* Tags */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {caseItem.tags.slice(0, 4).map((tag, tagIndex) => (
                          <span
                            key={tagIndex}
                            className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full hover:bg-[#7d2128]/10 hover:text-[#7d2128] transition-colors duration-200"
                          >
                            {tag}
                          </span>
                        ))}
                        {caseItem.tags.length > 4 && (
                          <span className="px-3 py-1 bg-gray-100 text-gray-500 text-sm rounded-full">
                            +{caseItem.tags.length - 4} mais
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Read More */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-[#7d2128] font-medium group-hover:translate-x-2 transition-transform duration-300">
                        <span className="mr-2">Ver case completo</span>
                        <i className="ri-arrow-right-line text-xl"></i>
                      </div>

                      <div className="flex items-center text-gray-400 text-sm">
                        <i className="ri-user-line mr-1"></i>
                        <span>{caseItem.author}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>

          {/* Botão Carregar Mais */}
          {visibleCases < sortedCases.length && (
            <div className="text-center mt-12">
              <button
                onClick={() => setVisibleCases(prev => prev + 3)}
                className="inline-flex items-center px-8 py-4 bg-[#7d2128] text-white font-semibold rounded-xl hover:bg-[#6b1c22] transition-all duration-200 hover:scale-105 cursor-pointer"
              >
                <i className="ri-add-line text-xl mr-3"></i>
                Carregar Mais Cases
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Projects Logos Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Projetos Realizados</h2>
            <p className="text-xl text-gray-600">Histórias de sucesso com soluçoes sob medidas ou produtos personalizados</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8">
            {Array.from({ length: 20 }, (_, i) => i + 1).map((index) => (
              <div key={index} className="group">
                <div className="relative bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer overflow-hidden">
                  {/* Degrade vertical */}
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-gray-100 opacity-60"></div>

                  {/* Logo com filtro preto e branco */}
                  <div className="relative z-10 flex items-center justify-center h-16">
                    <img
                      src={`/images/projetos/logo-${index.toString().padStart(2, '0')}.png`}
                      alt={`Projeto ${index}`}
                      className="max-w-full max-h-full object-contain filter grayscale contrast-75 group-hover:filter-none transition-all duration-300"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-gray-900 via-gray-800 to-[#161e2d] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Quer saber mais possibilidades?
          </h2>
          <p className="text-xl text-gray-200 mb-8">
            Descubra como podemos transformar seu negócio com nossas soluções inovadoras.
          </p>
          <a
            href="/contato"
            className="inline-flex items-center px-8 py-4 bg-white text-[#161e2d] font-semibold rounded-xl hover:bg-gray-100 transition-all duration-200 hover:scale-105"
          >
            <i className="ri-mail-send-line text-xl mr-3"></i>
            Fale com um Especialista
          </a>
        </div>
      </section>

      <Footer />

      <style>{`
        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(calc(-100% / 1.18));
          }
        }

        @keyframes scroll-mobile {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(calc(-100% * 4.9));
          }
        }

        @keyframes fade-in-up {
          0% {
            opacity: 0;
            transform: translateY(20px);
          }
          10% {
            opacity: 1;
            transform: translateY(0);
          }
          90% {
            opacity: 1;
            transform: translateY(0);
          }
          100% {
            opacity: 0;
            transform: translateY(-20px);
          }
        }

        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes move-right {
          0% {
            transform: translateX(-100px);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateX(calc(100vw + 100px));
            opacity: 0;
          }
        }

        @keyframes move-right-slow {
          0% {
            transform: translateX(-100px);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateX(calc(100vw + 100px));
            opacity: 0;
          }
        }

        @keyframes move-right-fast {
          0% {
            transform: translateX(-100px);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateX(calc(100vw + 100px));
            opacity: 0;
          }
        }

        @keyframes move-left {
          0% {
            transform: translateX(calc(100vw + 100px));
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateX(-100px);
            opacity: 0;
          }
        }

        @keyframes move-left-slow {
          0% {
            transform: translateX(calc(100vw + 100px));
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateX(-100px);
            opacity: 0;
          }
        }

        @keyframes move-left-fast {
          0% {
            transform: translateX(calc(100vw + 100px));
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateX(-100px);
            opacity: 0;
          }
        }

        @keyframes move-down {
          0% {
            transform: translateY(-100px);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateY(calc(100vh + 100px));
            opacity: 0;
          }
        }

        @keyframes move-down-slow {
          0% {
            transform: translateY(-100px);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateY(calc(100vh + 100px));
            opacity: 0;
          }
        }

        @keyframes move-down-fast {
          0% {
            transform: translateY(-100px);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateY(calc(100vh + 100px));
            opacity: 0;
          }
        }

        @keyframes move-up {
          0% {
            transform: translateY(calc(100vh + 100px));
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateY(-100px);
            opacity: 0;
          }
        }

        @keyframes move-up-slow {
          0% {
            transform: translateY(calc(100vh + 100px));
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateY(-100px);
            opacity: 0;
          }
        }

        @keyframes move-up-fast {
          0% {
            transform: translateY(calc(100vh + 100px));
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateY(-100px);
            opacity: 0;
          }
        }

        @keyframes rotate-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes rotate-reverse {
          from {
            transform: rotate(360deg);
          }
          to {
            transform: rotate(0deg);
          }
        }

        @keyframes rotate-fast {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes float {
          0%,
          100% {
            transform: translateY(0px) rotate(45deg);
          }
          50% {
            transform: translateY(-20px) rotate(45deg);
          }
        }

        @keyframes float-slow {
          0%,
          100% {
            transform: translateY(0px) rotate(45deg);
          }
          50% {
            transform: translateY(-15px) rotate(45deg);
          }
        }

        @keyframes float-fast {
          0%,
          100% {
            transform: translateY(0px) rotate(45deg);
          }
          50% {
            transform: translateY(-25px) rotate(45deg);
          }
        }

        .animate-scroll {
          animation: scroll 15s linear infinite;
        }

        @media (max-width: 1024px) {
          .animate-scroll {
            animation: scroll-mobile 30s linear infinite;
          }
        }

        .animate-scroll:hover {
          animation-play-state: paused;
        }

        .animate-fade-in-up {
          animation: fade-in-up 2.5s ease-in-out;
        }

        .animate-spin-slow {
          animation: spin-slow 2s linear infinite;
        }

        .animate-move-right {
          animation: move-right 15s linear infinite;
        }

        .animate-move-right-slow {
          animation: move-right-slow 20s linear infinite;
        }

        .animate-move-right-fast {
          animation: move-right-fast 10s linear infinite;
        }

        .animate-move-left {
          animation: move-left 15s linear infinite;
        }

        .animate-move-left-slow {
          animation: move-left-slow 20s linear infinite;
        }

        .animate-move-left-fast {
          animation: move-left-fast 10s linear infinite;
        }

        .animate-move-down {
          animation: move-down 18s linear infinite;
        }

        .animate-move-down-slow {
          animation: move-down-slow 25s linear infinite;
        }

        .animate-move-down-fast {
          animation: move-down-fast 12s linear infinite;
        }

        .animate-move-up {
          animation: move-up 18s linear infinite;
        }

        .animate-move-up-slow {
          animation: move-up-slow 25s linear infinite;
        }

        .animate-move-up-fast {
          animation: move-up-fast 12s linear infinite;
        }

        .animate-rotate-slow {
          animation: rotate-slow 30s linear infinite;
        }

        .animate-rotate-reverse {
          animation: rotate-reverse 25s linear infinite;
        }

        .animate-rotate-fast {
          animation: rotate-fast 15s linear infinite;
        }

        .animate-float {
          animation: float 6s ease-in-out infinite;
        }

        .animate-float-slow {
          animation: float-slow 8s ease-in-out infinite;
        }

        .animate-float-fast {
          animation: float-fast 4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default CasesPage;